import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "./api";

/**
 * The creative actions §4.1 requires on the Overview.
 *
 *   Creative Session Library   the offer's existing sessions, each opening
 *                              where it already opens
 *   Autonomous Creative        starts the guided workflow and lands in it
 *   Custom Creative Session    starts the write-it-yourself flow
 *
 * REACHING THE EXISTING HANDLERS, AND WHY THIS IS NOT A REWRITE
 * ============================================================
 * Both actions exist today inside `CreativeEngineDropdown` (`App.js:1390`).
 * That component is not exported and `App.js` is frozen tonight, so it cannot
 * be imported and must not gain an export. What matters for the Definition of
 * Done is that these buttons reach the SAME handlers — the same endpoints and
 * the same destinations — and they do, call for call:
 *
 *     library    GET  /api/creative/sessions?offer_id=
 *                ->  /creative-engine/:offerId?session=<id>
 *     new        POST /api/creative/sessions
 *                ->  /creative-engine/:offerId?session=<id>&new=1
 *
 * THE CHOOSER — CREATIVE-SESSION-UX-FIXES item 1, 2026-09-08
 * ==========================================================
 * This used to be ONE button calling `api.startCreativeSession`, which branches
 * on `REACT_APP_CCS_ENABLED`. The live value of that flag is `true` (read from
 * Render's env-var API, 2026-09-08), so the single button always created a
 * Shared Custom Creative Session and landed on `/creative?session=<id>`. The
 * operator was never shown the difference, and **v2 could not start an
 * Autonomous session by any route** — the chooser exists only in v1, where
 * `App.js`'s `CreativeLanding` renders CreativeHub for `mode === "autonomous"`
 * and CustomCreativeSession for `mode === "custom"`.
 *
 * There are now two buttons, one per flow, because the two flows are not
 * interchangeable and pretending otherwise is what hid the Autonomous path:
 *
 *     Autonomous   POST /api/creative/sessions
 *                  ->  /creative-engine/:offerId?session=<id>&new=1
 *                  via `startAutonomousCreativeSession`, which NEVER consults
 *                  the cutover flag. That is the whole reason it exists
 *                  separately — see its docstring, and the correction recorded
 *                  in CREATIVE-AUTONOMOUS-ROUTING.
 *
 *     Custom       `startCreativeSession`, unchanged, flag branch intact:
 *                  POST /api/ccs/sessions -> /creative?session=<id> when the
 *                  cutover is on, and the autonomous endpoint when it is off.
 *
 * The flag itself is untouched. Nothing here changes which session the CUSTOM
 * button produces; it changes only that the Autonomous one is now reachable.
 *
 * The destinations are v1 routes and stay v1 routes. Step 6 relocates the
 * creative components into the Workstation; until it does, these actions hand
 * the operator to the surfaces that work today (R2).
 *
 * CREATIVE WORKSPACE IS THE DEFAULT ENTRY — issue #107 (Founder, 2026-09-02)
 * =========================================================================
 * "Make Creative Workspace the DEFAULT starting point for all new creative
 * work." This is a SOFT default and an entry-steering change only: nothing
 * below is disabled or removed. Where a native Workspace slot exists
 * (`onOpenWorkspace`, i.e. the Ads & Creative tab), the Workspace button
 * becomes the primary action and is shown first; the legacy session starters
 * (Autonomous, Custom, the session library) move below it under a clear label
 * and stay one click away. Where no Workspace slot is passed (Overview),
 * behavior is unchanged — Autonomous stays primary — so that caller is
 * untouched. Retirement of the legacy surfaces is explicitly NOT in scope and
 * remains gated on the parity criteria in
 * `docs/department-decisions/01_CREATIVE_PRODUCTION.md`.
 */
export default function CreativeActions({ offerId, sessions, onOpenImageAds, onOpenWorkspace }) {
  const navigate = useNavigate();
  // When a native Workspace slot exists, Workspace is the default entry and
  // Autonomous is demoted to a reachable secondary. Without the slot (Overview)
  // Autonomous keeps its original primary styling — no behavior change there.
  const workspaceDefault = !!onOpenWorkspace;
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // One starter, told which flow to run. `kind` is carried into `creating` so
  // the two buttons disable independently and the operator can see WHICH one is
  // working — a shared boolean would grey out both and say nothing.
  const start = useCallback(
    async (kind) => {
      if (!offerId || creating) return;
      setCreating(kind);
      setError(null);
      const res =
        kind === "autonomous"
          ? await api.startAutonomousCreativeSession(offerId)
          : await api.startCreativeSession(offerId);
      setCreating(null);
      if (!res.ok) {
        // Shown in place, and it names WHICH flow failed. The v1 handler uses
        // `alert()`; a modal the shell cannot dismiss is worse than a line of
        // text next to the button that caused it.
        setError(
          `Could not start the ${kind === "autonomous" ? "Autonomous" : "Custom"} session: ` +
            (res.error || "no reason given"),
        );
        return;
      }
      // The local Custom redirect that used to sit here is GONE (2026-09-09).
      // It rewrote `res.path` for one kind, from one entry point — which is why
      // the Autonomous button next to it still bounced out of v2 into v1, and
      // why the App.js dropdown and the Creative Hub did too. The decision now
      // lives once, in `creativeSurfacePath`, so every entry point agrees and
      // this component simply navigates where it is told.
      navigate(res.path);
    },
    [offerId, creating, navigate],
  );

  const rows = sessions?.state === "ready"
    ? (sessions.data?.sessions || []).filter((s) => s.offer_id === offerId)
    : [];

  // Built once, ordered twice. The same buttons render in both callers; only
  // their order and which one is primary differ, driven by `workspaceDefault`.
  const libraryButton = (
    <button
      type="button"
      className="btn-ghost btn-sm"
      onClick={() => setLibraryOpen((v) => !v)}
      disabled={!offerId}
      aria-expanded={libraryOpen}
      data-testid="ws-creative-session-library"
    >
      Creative Session Library
      {sessions?.state === "ready" ? ` (${rows.length})` : ""}
    </button>
  );

  const autonomousButton = (
    <button
      type="button"
      className={workspaceDefault ? "btn-ghost btn-sm" : "btn-primary btn-sm"}
      onClick={() => start("autonomous")}
      disabled={!offerId || !!creating}
      title="The guided workflow: psychology map, angles, hooks, scripts, production."
      data-testid="ws-new-autonomous-session"
    >
      {creating === "autonomous" ? "Creating…" : "Autonomous Creative"}
    </button>
  );

  const customButton = (
    <button
      type="button"
      className="btn-ghost btn-sm"
      onClick={() => start("custom")}
      disabled={!offerId || !!creating}
      title="Write the hook, script and CTA yourself, with a chat collaborator."
      data-testid="ws-new-custom-session"
    >
      {creating === "custom" ? "Creating…" : "Custom Creative Session"}
    </button>
  );

  // Image Ads — issue #29 first connected this button to v2 by linking out to
  // the v1 Image Ad Studio (v1 route, App.js:4598). Issue #31 replaces the
  // link-out with a native open where the caller supports it: `AdsCreative.jsx`
  // passes `onOpenImageAds` and renders the same `ImageAdStudio` component
  // inline, so the operator never leaves v2. `onOpenImageAds` is optional so
  // `Overview.jsx`'s usage of this same component, which does not render a
  // native slot for it, keeps its original v1 link-out behavior unchanged.
  const imageButton = (
    <button
      type="button"
      className="btn-ghost btn-sm"
      onClick={() => (onOpenImageAds ? onOpenImageAds() : navigate(`/creative/image/${offerId}`))}
      disabled={!offerId}
      title="Generate a static image ad for this offer."
      data-testid="ws-open-image-ad-studio"
    >
      Image Ads
    </button>
  );

  // Creative Workspace — epic #46, made the default entry by issue #107.
  // Additive: does not change or remove any action above. Opens the shell where
  // one angle carries multiple bodies/concepts, image lane first. Only rendered
  // where the caller passes a native slot (`onOpenWorkspace`); there it is the
  // primary, first-listed action. Still marked (beta) because the Founder made
  // it the default while its capability parity with the legacy surfaces is not
  // yet proven — that honesty is the reason the legacy starters stay reachable
  // below.
  const workspaceButton = onOpenWorkspace ? (
    <button
      type="button"
      className="btn-primary btn-sm"
      onClick={() => onOpenWorkspace()}
      disabled={!offerId}
      title="Default starting point for new creative: one workspace per offer, an angle with multiple bodies/concepts."
      data-testid="ws-open-creative-workspace"
    >
      {"Start in Creative Workspace (beta)"}
    </button>
  ) : null;

  return (
    <div className="ws-creative-actions">
      {workspaceDefault ? (
        <>
          {workspaceButton}
          {/* The legacy session flows stay one click away, clearly labelled as
              the alternative to the default above. Nothing here is disabled. */}
          <p className="ws-creative-actions-legacy-label dim" data-testid="ws-legacy-starters-label">
            Or start a legacy session (still available while Workspace proves out):
          </p>
          {autonomousButton}
          {customButton}
          {libraryButton}
          {imageButton}
        </>
      ) : (
        <>
          {libraryButton}
          {autonomousButton}
          {customButton}
          {imageButton}
        </>
      )}

      {error ? (
        <p className="ws-action-error state-error" role="alert">
          {error}
        </p>
      ) : null}

      {libraryOpen ? (
        <div className="ws-session-library" data-testid="ws-session-library-list">
          {sessions?.state === "loading" ? (
            <p className="dim">Reading sessions…</p>
          ) : rows.length === 0 ? (
            <p className="dim">
              No creative sessions for this offer yet. The endpoint answered; it
              had nothing to return.
            </p>
          ) : (
            <ul className="ws-session-list">
              {rows.map((s) => (
                <li key={s.session_id}>
                  <button
                    type="button"
                    className="ws-session-item"
                    onClick={() => navigate(api.sessionPath(offerId, s.session_id))}
                    data-testid={`ws-session-${s.session_id}`}
                  >
                    <span>Session {s.session_number ?? "—"}</span>
                    <span className="ws-session-status dim">{s.status}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
